import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Store } from '@mui/icons-material';
import { getMerchants } from '../services/merchants';
import { getCategoryTypes } from '../services/categories';

const ShopFormDialog = ({ open, onClose, onSubmit, shop, isEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    ownerId: '',
    categoryTypeId: '',
    address1: '',
    location: '',
    state: '',
    pinCode: '',
    contactNumber: '',
    whatsappNumber: '',
    landlineNumber: '',
    email: '',
    latitude: '',
    longitude: '',
    status: 'active',
    is_featured: false,
    is_test_shop: false,
    isWheelchairAccessible: false,
  });

  const [merchants, setMerchants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch merchants and categories on dialog open
  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (shop) {
      setFormData({
        name: shop.name || '',
        ownerId: shop.ownerId || '',
        categoryTypeId: shop.categoryTypeId || '',
        address1: shop.address1 || '',
        location: shop.location || '',
        state: shop.state || '',
        pinCode: shop.pinCode || '',
        contactNumber: shop.contactNumber || '',
        whatsappNumber: shop.whatsappNumber || '',
        landlineNumber: shop.landlineNumber || '',
        email: shop.email || '',
        latitude: shop.latitude || '',
        longitude: shop.longitude || '',
        status: shop.status || 'active',
        is_featured: shop.is_featured === true || shop.is_featured === 1 || shop.is_featured === 'true' || shop.is_featured === '1',
        is_test_shop: shop.is_test_shop === true || shop.is_test_shop === 1 || shop.is_test_shop === 'true' || shop.is_test_shop === '1',
        isWheelchairAccessible: shop.isWheelchairAccessible === true || shop.isWheelchairAccessible === 1 || shop.isWheelchairAccessible === 'true' || shop.isWheelchairAccessible === '1',
      });
    } else {
      resetForm();
    }
    setErrors({});
  }, [shop, open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch merchants
      const merchantsResponse = await getMerchants();
      let merchantsData = [];
      if (Array.isArray(merchantsResponse)) {
        merchantsData = merchantsResponse;
      } else if (merchantsResponse && Array.isArray(merchantsResponse.data)) {
        merchantsData = merchantsResponse.data;
      } else if (merchantsResponse?.data?.data) {
        merchantsData = merchantsResponse.data.data
      }
      setMerchants(merchantsData);

      // Fetch categories
      const categoriesResponse = await getCategoryTypes();
      let categoriesData = [];
      if (Array.isArray(categoriesResponse)) {
        categoriesData = categoriesResponse;
      } else if (categoriesResponse && Array.isArray(categoriesResponse.data)) {
        categoriesData = categoriesResponse.data;
      }
      setCategories(categoriesData);
    } catch (error) {
      setErrors({ general: 'Failed to load data' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      ownerId: '',
      categoryTypeId: '',
      address1: '',
      location: '',
      state: '',
      pinCode: '',
      contactNumber: '',
      whatsappNumber: '',
      landlineNumber: '',
      email: '',
      latitude: '',
      longitude: '',
      status: 'active',
      is_featured: false,
      is_test_shop: false,
      isWheelchairAccessible: false,
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Shop name is required';
    }
    if (!formData.ownerId) {
      newErrors.ownerId = 'Owner is required';
    }
    if (!formData.categoryTypeId) {
      newErrors.categoryTypeId = 'Category type is required';
    }
    if (!formData.address1.trim()) {
      newErrors.address1 = 'Address is required';
    }
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    } else if (!/^[0-9]{10}$/.test(formData.contactNumber)) {
      newErrors.contactNumber = 'Enter a valid 10-digit phone number';
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit({
        ...formData,
        ownerId: parseInt(formData.ownerId),
        categoryTypeId: parseInt(formData.categoryTypeId),
        is_featured: Boolean(formData.is_featured),
        is_test_shop: Boolean(formData.is_test_shop),
        isWheelchairAccessible: Boolean(formData.isWheelchairAccessible),
      });
    }
  };

  return (
    <Dialog
      open={open}
            onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose(event, reason);
        }
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#374151',
        }}
      >
        <Store sx={{ color: '#52ad83' }} />
        {isEdit ? 'Edit Shop' : 'Add New Shop'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#52ad83' }} />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Shop Name */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Shop Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="Enter shop name"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Owner Dropdown */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth error={!!errors.ownerId}>
                  <InputLabel
                    sx={{
                      color: '#6b7280',
                      '&.Mui-focused': { color: '#52ad83' },
                    }}
                  >
                    Owner (Mobile Number)
                  </InputLabel>
                  <Select
                    name="ownerId"
                    value={formData.ownerId}
                    label="Owner (Mobile Number)"
                    onChange={handleChange}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#52ad83',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: 2,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Owner</em>
                    </MenuItem>
                    {merchants.map((merchant) => (
                      <MenuItem key={merchant.id} value={merchant.id}>
                        {merchant.number || merchant.phone || merchant.owner}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors.ownerId && (
                  <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                    {errors.ownerId}
                  </Typography>
                )}
              </Grid>

              {/* Category Type Dropdown */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth error={!!errors.categoryTypeId}>
                  <InputLabel
                    sx={{
                      color: '#6b7280',
                      '&.Mui-focused': { color: '#52ad83' },
                    }}
                  >
                    Category Type
                  </InputLabel>
                  <Select
                    name="categoryTypeId"
                    value={formData.categoryTypeId}
                    label="Category Type"
                    onChange={handleChange}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#52ad83',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: 2,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Category</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}{category.is_test ? ' (Test)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors.categoryTypeId && (
                  <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                    {errors.categoryTypeId}
                  </Typography>
                )}
              </Grid>

              {/* Address */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address1"
                  value={formData.address1}
                  onChange={handleChange}
                  error={!!errors.address1}
                  helperText={errors.address1}
                  placeholder="Enter street address"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Location */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  error={!!errors.location}
                  helperText={errors.location}
                  placeholder="e.g., Downtown"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* State */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="e.g., NY"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Pin Code */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Pin Code"
                  name="pinCode"
                  value={formData.pinCode}
                  onChange={handleChange}
                  placeholder="e.g., 10001"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Contact Number */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  error={!!errors.contactNumber}
                  helperText={errors.contactNumber}
                  placeholder="Enter 10-digit phone number"
                  inputProps={{ maxLength: 10 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* WhatsApp Number */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="WhatsApp Number"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  placeholder="Enter WhatsApp number"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Landline Number */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Landline Number"
                  name="landlineNumber"
                  value={formData.landlineNumber}
                  onChange={handleChange}
                  placeholder="Enter landline number"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  placeholder="shop@example.com"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Latitude */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  placeholder="e.g., 40.7128"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Longitude */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  placeholder="e.g., -74.0060"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Status */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  select
                  SelectProps={{ native: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </TextField>
              </Grid>
               {/* is featured */}
               <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_featured}
                      onChange={handleChange}
                      name="is_featured"
                      sx={{
                        color: '#52ad83',
                        '&.Mui-checked': {
                          color: '#52ad83',
                        },
                      }}
                    />
                  }
                  label="Featured"
                />
              </Grid>
              {/* is test shop */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_test_shop}
                      onChange={handleChange}
                      name="is_test_shop"
                      sx={{
                        color: '#52ad83',
                        '&.Mui-checked': {
                          color: '#52ad83',
                        },
                      }}
                    />
                  }
                  label="Test Shop"
                />
              </Grid>
              {/* Wheelchair Accessible */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isWheelchairAccessible}
                      onChange={handleChange}
                      name="isWheelchairAccessible"
                      sx={{
                        color: '#52ad83',
                        '&.Mui-checked': {
                          color: '#52ad83',
                        },
                      }}
                    />
                  }
                  label="Wheelchair Accessible"
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: '#6b7280',
            '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
            boxShadow: '0 4px 15px rgba(82, 173, 131, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3d8f64 0%, #52ad83 100%)',
              boxShadow: '0 6px 20px rgba(82, 173, 131, 0.4)',
            },
          }}
        >
          {isEdit ? 'Update Shop' : 'Create Shop'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShopFormDialog;
